import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { formatNumber } from "@progress/kendo-intl";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { DateInput, DateInputProps, DateRangePicker, SelectionRange } from "@progress/kendo-react-dateinputs";
import { MultiSelect, DropDownList } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { arrowRotateCwIcon, filterClearIcon } from "@progress/kendo-svg-icons";
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchApi } from "services/api";
import { LoadOneReconnectPolicy } from "services/signalr/retryPolicy";
import { ApiLink } from "TypeGen/api-link";
import { IDNameViewModel } from "TypeGen/id-name-view-model";
import { OrderServiceExceptionStatus } from "TypeGen/order-service-exception-status";
import { OrderServiceFailureReason } from "TypeGen/order-service-failure-reason";
import { OrderServiceExceptionViewModel } from "TypeGen/ServiceExceptions/order-service-exception-view-model";
import { ViewModel } from "TypeGen/ServiceExceptions/view-model";
import { ILink } from "types/link";
import { Title } from "utils/title";
import useLocalStorage from "utils/useLocalStorage";
import TripSummary from "views/Track/TripSummary";
import MultipleCustomerInput, { CustomerInputValue } from "../../components/MultipleCustomerInput";
import { AssignedCell } from "./AssignedCell";
import AssignResolvePopup from "./AssignResolvePopup";
import { DetailComponent } from "./DetailComponent";
import { EscalationSOPCell } from "./EscalationSOPCell";
import { FailureReasonCell } from "./FailureReasonCell";
import { OpenedCell } from "./OpenedCell";
import { ResolvedCell } from "./ResolvedCell";
import { FailureReasonViewModel } from "TypeGen/ServiceExceptions/failure-reason-view-model";

const DateRangeStartDate = dayjs().subtract(2, "days").startOf('day').toDate();
const DateRangeEndDate = dayjs().add(1, "days").startOf('day').toDate();

type NewOrderServiceException = {
  OrderServiceExceptionID: number;
  FailureReasonID: number;
}

type AssignedOrderServiceException = {
  OrderServiceExceptionID: number;
  FailureReasonID: number;
}

type ResolvedOrderServiceException = {
  OrderServiceExceptionID: number;
  FailureReasonID: number;
}

type ServiceExceptionMetricsResponse = {
  TotalOpen: number;
  TotalAssigned: number;
  TotalResolved: number;
  AvgTimeToAssign: ServiceExceptionMetricPeriod;
  AvgTimeToResolve: ServiceExceptionMetricPeriod;
  SLAPercentageMet: ServiceExceptionMetricPeriod;
}

type ServiceExceptionMetricPeriod = {
  Today: number | null;
  Week: number | null;
  Month: number | null;
}

const CustomDateInput = (props: DateInputProps) => {
  return (
    <DateInput {...props} label={undefined} width={92} formatPlaceholder={{ year: 'yyyy', month: 'MM', day: 'dd' }} />
  );
};

export const locationFilters: { Name: string, Filter: string }[] = [
  { Name: 'DTW', Filter: 'DTW' },
  { Name: 'DTW1', Filter: 'DTW1' },
  { Name: 'DTW2', Filter: 'DTW2' },
  { Name: 'DTW3', Filter: 'DTW3' },
  { Name: 'DTW4', Filter: 'DTW4' },
  { Name: 'DTW5', Filter: 'DTW5' },
  { Name: 'DTW6', Filter: 'DTW6' },
  { Name: 'DTW7', Filter: 'DTW7' },
  { Name: 'DTW8', Filter: 'DTW8' },
  { Name: 'DTW9', Filter: 'DTW9' },
  { Name: 'DTW10', Filter: 'DTW10' },
  { Name: 'DTW11', Filter: 'DTW11' },
  { Name: 'Taylor', Filter: 'DTW*' },
  { Name: 'Mexico', Filter: '*MX*' },
  { Name: 'Charlotte', Filter: 'CLT' },
  { Name: 'Tennessee', Filter: 'TN' },
  { Name: 'Dayton', Filter: 'DAY' },
]

const assetTypeFilters = ['All Loads', 'Asset Loads', 'Carrier Loads'];

const ServiceExceptions = () => {
  const [serviceExceptions, setServiceExceptions] = useState<OrderServiceExceptionViewModel[]>([]);
  const [metrics, setMetrics] = useState<ServiceExceptionMetricsResponse>(null);
  const [connection, setConnection] = useState<HubConnection>(null);

  const initialLoadRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState<IDNameViewModel[]>([]);
  const [serviceTeams, setServiceTeams] = useState<IDNameViewModel[]>([]);
  const [failureReasons, setFailureReasons] = useState<FailureReasonViewModel[]>([]);
  const [showTripSummary, setShowTripSummary] = useState(0);
  const [showAssignResolvePopup, setShowAssignResolvePopup] = useState<ILink>(null);
  const [showAssignResolvePopupUserId, setShowAssignResolvePopupUserId] = useState<string>(null);
  const signalRRefreshTimeoutRef = useRef<number | null>(null);

  // Filters
  const [filterByDateRange, setFilterByDateRange] = useState<SelectionRange>({
    start: DateRangeStartDate,
    end: DateRangeEndDate,
  });
  const [filterAssignedToMe, setFilterAssignedToMe] = useLocalStorage<boolean | null>("ServiceExceptions_AssignedToMe", false);
  const [filterStatuses, setFilterStatuses] = useLocalStorage<number[]>("ServiceExceptions_Statuses", [OrderServiceExceptionStatus.Open, OrderServiceExceptionStatus.Assigned]);
  const [filterFailureReasons, setFilterFailureReasons] = useLocalStorage<number[]>("ServiceExceptions_FailureReasons", [OrderServiceFailureReason.TrackingLate, OrderServiceFailureReason.TrackingNeedsCheckCall, OrderServiceFailureReason.TrackingWaitingTime, OrderServiceFailureReason.TrackingNeedsCustomerUpdate, OrderServiceFailureReason.TrackingHCPodNeeded]);
  const [filterServiceTeams, setFilterServiceTeams] = useLocalStorage<number[]>("ServiceExceptions_ServiceTeams", []);
  const [filterReference3, setFilterReference3] = useLocalStorage<string[]>("ServiceExceptions_Reference3", []);
  const [orderNumber, setOrderNumber] = useLocalStorage<number>("ServiceExceptions_OrderNumber", null);
  const [selectedCustomers, setSelectedCustomers] = useLocalStorage<CustomerInputValue[]>("ServiceExceptions_Customers", []);
  const [filterAssetType, setFilterAssetType] = useLocalStorage<string>("ServiceExceptions_AssetTypeFilter", "All Loads");

  const reset = () => {
    setFilterByDateRange({
      start: DateRangeStartDate,
      end: DateRangeEndDate,
    });
    setFilterAssignedToMe(false);
    setFilterStatuses([OrderServiceExceptionStatus.Open, OrderServiceExceptionStatus.Assigned]);
    setFilterFailureReasons([OrderServiceFailureReason.TrackingLate, OrderServiceFailureReason.TrackingNeedsCheckCall, OrderServiceFailureReason.TrackingWaitingTime, OrderServiceFailureReason.TrackingNeedsCustomerUpdate, OrderServiceFailureReason.TrackingHCPodNeeded]);
    setFilterServiceTeams([]);
    setFilterReference3([]);
    setFilterAssetType("All Loads");
  }

  const fetchData = useCallback(() => {
    if (!filterByDateRange || !filterByDateRange.start || !filterByDateRange.end) {
      return;
    }

    if (orderNumber && orderNumber < 1000000) {
      return;
    }

    setLoading(true);
    fetchApi('/api/Order/ServiceExceptions', {
      FromDate: filterByDateRange.start,
      ToDate: filterByDateRange.end,
      AssignedToMe: filterAssignedToMe,
      StatusID: filterStatuses,
      FailureReasonID: filterFailureReasons,
      ServiceTeamID: filterServiceTeams,
      Reference3: filterReference3,
      AssetTypeFilter: filterAssetType,
      OrderNumber: orderNumber,
      CustomerIDs: selectedCustomers.map(x => x.CustomerID),
    }, 'POST')
      .then((response: ViewModel) => {
        setStatuses(response.Statuses);
        setFailureReasons(response.FailureReasons);
        setServiceTeams(response.ServiceTeams);
        setServiceExceptions(response.ServiceExceptions);

        const maxFetchedId = Math.max(0, ...response.ServiceExceptions.map(x => x.OrderServiceExceptionID));
        const storedMaxId = parseInt(localStorage.getItem("ServiceExceptions_LastMaxID") || "0");
        if (initialLoadRef.current) {
          // On the initial load, simply update localStorage if the fetched max is higher.
          if (maxFetchedId > storedMaxId) {
            localStorage.setItem("ServiceExceptions_LastMaxID", maxFetchedId.toString());
          }
          initialLoadRef.current = false;
        } else if (maxFetchedId > storedMaxId) {
          // On subsequent updates, if the fetched id is higher, notify and update localStorage.
          localStorage.setItem("ServiceExceptions_LastMaxID", maxFetchedId.toString());
          const newException = response.ServiceExceptions.find(x => x.OrderServiceExceptionID === maxFetchedId);
          if (newException && "Notification" in window) {
            Notification.requestPermission().then((permission) => {
              if (permission !== "denied") {
                new Notification("New Service Exception", { body: newException.LatestNote });
              }
            });
          }
        }
      })
      .finally(() => setLoading(false));
  }, [filterByDateRange, filterAssignedToMe, filterStatuses, filterFailureReasons, filterServiceTeams, filterReference3, filterAssetType, orderNumber, selectedCustomers]);

  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData; // Keep the ref updated with the latest fetchData
  }, [fetchData]);

  const filterFailureReasonsRef = useRef(filterFailureReasons);
  useEffect(() => {
    filterFailureReasonsRef.current = filterFailureReasons;
  }, [filterFailureReasons]);

  const shouldRefreshForServiceExceptionEvent = useCallback((event: NewOrderServiceException | AssignedOrderServiceException | ResolvedOrderServiceException) => {
    // Small issue here OrderNumber bypasses failure reason filter
    return filterFailureReasonsRef.current.length === 0 || filterFailureReasonsRef.current.includes(event.FailureReasonID);
  }, []);

  const scheduleSignalRRefresh = useCallback(() => {
    if (signalRRefreshTimeoutRef.current) {
      return;
    }

    signalRRefreshTimeoutRef.current = window.setTimeout(() => {
      signalRRefreshTimeoutRef.current = null;
      fetchDataRef.current();
    }, 1000 + (Math.random() * 2000)); // Random delay between 1-3 seconds
  }, []);

  const fetchMetrics = useCallback(() => {
    if (!filterByDateRange || !filterByDateRange.start || !filterByDateRange.end) {
      return;
    }
    fetchApi('/api/Order/ServiceExceptionMetrics', {
      FromDate: filterByDateRange.start,
      ToDate: filterByDateRange.end,
      FailureReasonID: filterFailureReasons
    }, 'POST')
      .then((response: ServiceExceptionMetricsResponse) => {
        setMetrics(response);
      });
  }, [filterByDateRange, filterFailureReasons]);

  useEffect(() => {
    if (!connection) return;

    async function startConnection() {
      try {
        connection.onreconnected(() => {
          fetchDataRef.current();
        });

        connection.on('newOrderServiceException', (event: NewOrderServiceException) => {
          if (shouldRefreshForServiceExceptionEvent(event)) {
            scheduleSignalRRefresh();
          }
        });

        connection.on('assignedOrderServiceException', (event: AssignedOrderServiceException) => {
          if (shouldRefreshForServiceExceptionEvent(event)) {
            scheduleSignalRRefresh();
          }
        });

        connection.on('resolvedOrderServiceException', (event: ResolvedOrderServiceException) => {
          if (shouldRefreshForServiceExceptionEvent(event)) {
            scheduleSignalRRefresh();
          }
        });

        await connection.start();
      } catch (error) {
        console.error(error);
      }
    }

    if (connection) {
      startConnection();
    }

    return () => {
      if (signalRRefreshTimeoutRef.current) {
        clearTimeout(signalRRefreshTimeoutRef.current);
        signalRRefreshTimeoutRef.current = null;
      }
      connection?.stop();
    }
  }, [connection, scheduleSignalRRefresh, shouldRefreshForServiceExceptionEvent]);

  const handleShowAssignResolvePopup = (link: ApiLink, existingUserId: string = null) => {
    setShowAssignResolvePopup(link);
    setShowAssignResolvePopupUserId(existingUserId);
  }

  useEffect(() => {
    const builder = new HubConnectionBuilder()
      .withAutomaticReconnect(new LoadOneReconnectPolicy())
      .withUrl("/hub/order")
      .configureLogging(LogLevel.Warning)
      .build();

    setConnection(builder);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return <>
    <Title string="Service Exceptions" />
    {metrics && <div className="row my-2">
      <div className="col-md-3">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Total Exceptions</h5>
            <h6 className="card-subtitle mb-2 text-muted">Open / Assigned / Resolved</h6>
            <h1 className="card-text">{metrics.TotalOpen} / {metrics.TotalAssigned} / {metrics.TotalResolved}</h1>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Avg Minutes to Assign</h5>
            <h6 className="card-subtitle mb-2 text-muted">Today / Week / Month</h6>
            <h1 title="Today / Week / Month" className="card-text">{metrics.AvgTimeToAssign.Today || "NA"} / {metrics.AvgTimeToAssign.Week || "NA"} / {metrics.AvgTimeToAssign.Month || "NA"}</h1>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Avg Minutes to Resolve</h5>
            <h6 className="card-subtitle mb-2 text-muted">Today / Week / Month</h6>
            <h1 title="Today / Week / Month" className="card-text">{metrics.AvgTimeToResolve.Today || "NA"} / {metrics.AvgTimeToResolve.Week || "NA"} / {metrics.AvgTimeToResolve.Month || "NA"}</h1>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="card">
          <div className="card-body">
            <Tooltip anchorElement="target" position="top">
              <h5 title="5 Minute SLA" className="card-title">SLA% Met</h5>
            </Tooltip>
            <h6 className="card-subtitle mb-2 text-muted">Today / Week / Month</h6>
            <h1 title="Today / Week / Month" className="card-text">{formatNumber(metrics.SLAPercentageMet.Today || 0, "P1")} / {formatNumber(metrics.SLAPercentageMet.Week || 0, "P1")} / {formatNumber(metrics.SLAPercentageMet.Month || 0, "P1")}</h1>
          </div>
        </div>
      </div>
    </div>}
    <Grid
      sortable
      autoProcessData={{ sort: true }}
      data={serviceExceptions}
      detail={DetailComponent}
      dataItemKey="OrderServiceExceptionID"
      showLoader={loading}
    >
      <GridToolbar overflow="none">
        <Button
          title="Reset Filters"
          icon="filter-clear"
          svgIcon={filterClearIcon}
          onClick={reset}
        />
        <Button
          title="Refresh"
          icon="refresh"
          svgIcon={arrowRotateCwIcon}
          onClick={fetchData}
        />
        <Button
          togglable={true}
          selected={filterAssignedToMe}
          onClick={() => setFilterAssignedToMe(!filterAssignedToMe)}
        >
          {filterAssignedToMe ? "Mine" : "All"}
        </Button>
        <DateRangePicker
          style={{ width: 195, gap: "0.3em" }}
          startDateInput={CustomDateInput}
          endDateInput={CustomDateInput}
          value={filterByDateRange}
          onChange={(e) => {
            setFilterByDateRange(e.value);
            if (e.value && e.value.start && e.value.end) {
              fetchData();
            }
          }}
        />
        <ButtonGroup>
          {statuses.map((status) => <Button
            key={status.ID}
            togglable={true}
            selected={filterStatuses.includes(status.ID)}
            onClick={() => {
              if (filterStatuses.includes(status.ID)) {
                setFilterStatuses(filterStatuses.filter((id) => id !== status.ID));
              } else {
                setFilterStatuses([...filterStatuses, status.ID]);
              }
            }}
          >
            {status.Name}
          </Button>)}
        </ButtonGroup>
        <ButtonGroup>
          {serviceTeams.map((teamId) => <Button
            key={teamId.ID}
            togglable={true}
            selected={filterServiceTeams.includes(teamId.ID)}
            onClick={() => {
              if (filterServiceTeams.includes(teamId.ID)) {
                setFilterServiceTeams(filterServiceTeams.filter((id) => id !== teamId.ID));
              } else {
                setFilterServiceTeams([...filterServiceTeams, teamId.ID]);
              }
            }}
          >
            {teamId.Name}
          </Button>)}
        </ButtonGroup>
        <DropDownList
          data={assetTypeFilters}
          value={filterAssetType}
          onChange={(e) => setFilterAssetType(e.value)}
          style={{ width: 140 }}
        />
        <ButtonGroup>
          {locationFilters.map((location) => <Button
            key={location.Name}
            togglable={true}
            selected={filterReference3.includes(location.Filter)}
            title={location.Filter}
            onClick={() => {
              // These are mutually exclusive filters
              const isTaylor = location.Filter === "DTW*";
              const isDtwFamily = location.Filter.startsWith("DTW") && !isTaylor;
              setFilterReference3((prev) => {
                if (prev.includes(location.Filter)) {
                  return prev.filter((x) => x !== location.Filter);
                } else {
                  let updated = [...prev, location.Filter];
                  if (isTaylor) {
                    // Remove DTW and DTW1..DTW11 when selecting Taylor
                    updated = updated.filter(x => !(x.startsWith("DTW") && x !== "DTW*"));
                  } else if (isDtwFamily) {
                    // Remove Taylor when selecting any of the DTW1..DTW11
                    updated = updated.filter(x => x !== "DTW*");
                  }
                  return updated;
                }
              });
            }}
          >
            {location.Name}
          </Button>)}
        </ButtonGroup>
        <MultiSelect
          data={failureReasons}
          textField="Name"
          dataItemKey="ID"
          groupField="GroupName"
          value={failureReasons.filter((x) => filterFailureReasons.includes(x.ID))}
          onChange={(e) => {
            setFilterFailureReasons(e.target.value.map((x: FailureReasonViewModel) => x.ID));
          }}
          style={{ width: 360 }}
          placeholder="Failure Reasons"
        />
        <NumericTextBox
          value={orderNumber}
          placeholder='Order Number'
          min={0}
          spinners={false}
          width={130}
          format="0"
          onChange={(e) => {
            setOrderNumber(e?.value);
          }}
        />
        <MultipleCustomerInput
          value={selectedCustomers}
          onChange={(e) => {
            setSelectedCustomers(e);
          }}
          onCreditHold={false}
        />
      </GridToolbar>
      <Column field="FailureReasonName" title="Type" cells={{ data: FailureReasonCell }} width={150} />
      <Column field="OrderNumber" width={110} title="Order #" cells={{ data: (props) => <td {...props.tdProps} onClick={() => setShowTripSummary(props.dataItem.OrderID)} style={{ cursor: "pointer", color: "#007bff" }}>{props.dataItem.OrderNumber}</td> }} />
      <Column field="LatestNote" title="Latest Note" />
      <Column field="CreatedByFullName" title="Opened" cells={{ data: OpenedCell }} width={150} />
      <Column field="AssignedToUserFullName" title="Assigned" cells={{ data: (e) => AssignedCell({ ...e, assign: handleShowAssignResolvePopup, reassign: handleShowAssignResolvePopup }) }} width={150} />
      <Column field="ResolvedByFullName" title="Resolved" cells={{ data: (e) => ResolvedCell({ ...e, resolve: handleShowAssignResolvePopup }) }} width={150} />
      <Column field="HasEscalationSOP" title="SOP" cells={{ data: EscalationSOPCell }} width={50} />
    </Grid>
    {showTripSummary > 0 && <TripSummary orderId={showTripSummary} close={() => setShowTripSummary(0)} />}
    {showAssignResolvePopup && <AssignResolvePopup link={showAssignResolvePopup} userId={showAssignResolvePopupUserId} close={() => setShowAssignResolvePopup(null)} refresh={fetchData} />}
  </>;
}

export default ServiceExceptions;
